import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Upload, Brain, FileText, Loader2, Check, Copy, Sparkles } from 'lucide-react';
import Layout from '@/components/Layout';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function SmartUpload() {
  const { i18n } = useTranslation();
  const isAr = i18n.language === 'ar';
  const [file, setFile] = useState(null);
  const [textInput, setTextInput] = useState('');
  const [extractType, setExtractType] = useState('auto');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [mode, setMode] = useState('file'); // file or text
  const [copied, setCopied] = useState(false);

  const handleFileUpload = async () => {
    if (!file) return;
    setLoading(true);
    setResult(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('extract_type', extractType);

      const token = localStorage.getItem('token');
      const res = await fetch(`${BACKEND_URL}/api/extract/smart`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });
      const data = await res.json();
      setResult(data);
    } catch (error) { console.error('Extract failed:', error); setResult({ error: 'Extraction failed' }); }
    finally { setLoading(false); }
  };

  const handleTextExtract = async () => {
    if (!textInput.trim()) return;
    setLoading(true);
    setResult(null);
    try {
      const formData = new FormData();
      formData.append('text', textInput);
      formData.append('extract_type', extractType);
      formData.append('filename', 'pasted_text.txt');

      const token = localStorage.getItem('token');
      const res = await fetch(`${BACKEND_URL}/api/extract/text`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });
      const data = await res.json();
      setResult(data);
    } catch (error) { console.error('Extract failed:', error); setResult({ error: 'Extraction failed' }); }
    finally { setLoading(false); }
  };

  const handleCopy = () => {
    if (result?.extracted_data) {
      navigator.clipboard.writeText(typeof result.extracted_data === 'string' ? result.extracted_data : JSON.stringify(result.extracted_data, null, 2));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const typeLabels = isAr
    ? { auto: 'تلقائي (ذكاء اصطناعي)', tenant: 'بيانات مستأجر / عقد', property: 'بيانات عقار', maintenance: 'طلب صيانة', financial: 'بيانات مالية' }
    : { auto: 'Auto-detect (AI)', tenant: 'Tenant / Lease data', property: 'Property data', maintenance: 'Maintenance request', financial: 'Financial data' };

  const parseResult = () => {
    if (!result?.extracted_data) return null;
    const data = result.extracted_data;
    if (typeof data === 'string') {
      try { return JSON.parse(data); } catch { return data; }
    }
    return data;
  };

  const parsed = parseResult();

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 gradient-gold rounded-xl flex items-center justify-center">
            <Sparkles className="h-5 w-5 text-white" strokeWidth={1.5} />
          </div>
          <div>
            <h1 className="font-heading text-2xl font-bold text-gray-900">{isAr ? 'الإدخال الذكي' : 'Smart Data Entry'}</h1>
            <p className="text-sm text-gray-500">{isAr ? 'ارفع مستنداً أو الصق نصاً والذكاء الاصطناعي يستخرج البيانات تلقائياً' : 'Upload a document or paste text — AI extracts data automatically'}</p>
          </div>
        </div>

        {/* How it works */}
        <div className="grid grid-cols-3 gap-4">
          {[
            { step: '1', title: isAr ? 'ارفع أو الصق' : 'Upload or Paste', desc: isAr ? 'ملف عقد أو نص مستند' : 'Contract file or text' },
            { step: '2', title: isAr ? 'تحليل ذكي' : 'AI Analyzes', desc: isAr ? 'يستخرج البيانات تلقائياً' : 'Extracts data automatically' },
            { step: '3', title: isAr ? 'بيانات جاهزة' : 'Ready Data', desc: isAr ? 'انسخ واستخدم مباشرة' : 'Copy and use directly' }
          ].map((s, i) => (
            <Card key={i} className="shadow-soft border-gray-100">
              <CardContent className="pt-5 pb-4 text-center">
                <div className="w-8 h-8 gradient-primary rounded-full flex items-center justify-center mx-auto mb-2 text-white text-sm font-bold">{s.step}</div>
                <div className="font-medium text-sm text-gray-900">{s.title}</div>
                <div className="text-xs text-gray-500 mt-0.5">{s.desc}</div>
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Input Panel */}
          <Card className="shadow-soft border-gray-100">
            <CardHeader className="pb-3">
              <CardTitle className="font-heading text-base">{isAr ? 'مصدر البيانات' : 'Data Source'}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Mode Toggle */}
              <div className="flex gap-2">
                <button onClick={() => setMode('file')} className={`flex-1 py-2.5 rounded-xl text-sm font-medium transition-all ${mode === 'file' ? 'bg-primary text-white' : 'bg-gray-100 text-gray-600'}`}>
                  <Upload className="h-4 w-4 inline me-1.5" /> {isAr ? 'رفع ملف' : 'Upload File'}
                </button>
                <button onClick={() => setMode('text')} className={`flex-1 py-2.5 rounded-xl text-sm font-medium transition-all ${mode === 'text' ? 'bg-primary text-white' : 'bg-gray-100 text-gray-600'}`}>
                  <FileText className="h-4 w-4 inline me-1.5" /> {isAr ? 'لصق نص' : 'Paste Text'}
                </button>
              </div>

              {/* Extract Type */}
              <div>
                <Label className="text-xs text-gray-500">{isAr ? 'نوع البيانات المطلوبة' : 'Data Type to Extract'}</Label>
                <Select value={extractType} onValueChange={setExtractType}>
                  <SelectTrigger className="mt-1 rounded-xl"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(typeLabels).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              {/* File Upload */}
              {mode === 'file' && (
                <div>
                  <Label className="text-xs text-gray-500">{isAr ? 'اختر الملف' : 'Choose File'}</Label>
                  <div className="mt-1 border-2 border-dashed border-gray-200 rounded-2xl p-8 text-center hover:border-primary/40 transition-colors cursor-pointer" onClick={() => document.getElementById('file-input').click()}>
                    <input id="file-input" type="file" className="hidden" accept=".txt,.csv,.pdf,.doc,.docx,.json,.xml" onChange={e => setFile(e.target.files[0])} />
                    <Upload className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                    {file ? (
                      <div>
                        <p className="text-sm font-medium text-primary">{file.name}</p>
                        <p className="text-xs text-gray-400 mt-1">{(file.size / 1024).toFixed(1)} KB</p>
                      </div>
                    ) : (
                      <div>
                        <p className="text-sm text-gray-500">{isAr ? 'اسحب الملف هنا أو اضغط للاختيار' : 'Drag file here or click to select'}</p>
                        <p className="text-xs text-gray-400 mt-1">.txt, .csv, .pdf, .doc, .json</p>
                      </div>
                    )}
                  </div>
                  <Button onClick={handleFileUpload} disabled={!file || loading} className="w-full mt-4 gradient-primary text-white rounded-xl">
                    {loading ? <Loader2 className="h-4 w-4 animate-spin me-2" /> : <Brain className="h-4 w-4 me-2" />}
                    {isAr ? 'استخراج البيانات' : 'Extract Data'}
                  </Button>
                </div>
              )}

              {/* Text Input */}
              {mode === 'text' && (
                <div>
                  <Label className="text-xs text-gray-500">{isAr ? 'الصق المحتوى هنا' : 'Paste content here'}</Label>
                  <textarea
                    className="w-full mt-1 border border-gray-200 rounded-xl p-4 text-sm min-h-[200px] focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary/40"
                    value={textInput}
                    onChange={e => setTextInput(e.target.value)}
                    placeholder={isAr ? 'الصق نص العقد أو بيانات المستأجر هنا...\n\nمثال:\nالاسم: أحمد محمد\nرقم الهوية: 1234567890\nالإيجار: 5000 ريال\nتاريخ البداية: 2025-01-01\nتاريخ النهاية: 2026-01-01' : 'Paste contract text or tenant data here...\n\nExample:\nName: Ahmed Mohammed\nID: 1234567890\nRent: 5000 SAR\nStart: 2025-01-01\nEnd: 2026-01-01'}
                    dir="auto"
                  />
                  <Button onClick={handleTextExtract} disabled={!textInput.trim() || loading} className="w-full mt-4 gradient-primary text-white rounded-xl">
                    {loading ? <Loader2 className="h-4 w-4 animate-spin me-2" /> : <Brain className="h-4 w-4 me-2" />}
                    {isAr ? 'استخراج البيانات' : 'Extract Data'}
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Results Panel */}
          <Card className="shadow-soft border-gray-100">
            <CardHeader className="pb-3 flex flex-row items-center justify-between">
              <CardTitle className="font-heading text-base flex items-center gap-2">
                <Brain className="h-4 w-4 text-primary" /> {isAr ? 'البيانات المستخرجة' : 'Extracted Data'}
              </CardTitle>
              {result && (
                <Button variant="outline" size="sm" className="rounded-lg" onClick={handleCopy}>
                  {copied ? <Check className="h-3.5 w-3.5 text-green-600" /> : <Copy className="h-3.5 w-3.5" />}
                  <span className="ms-1.5 text-xs">{copied ? (isAr ? 'تم النسخ' : 'Copied') : (isAr ? 'نسخ' : 'Copy')}</span>
                </Button>
              )}
            </CardHeader>
            <CardContent>
              {loading && (
                <div className="flex flex-col items-center justify-center py-16">
                  <Loader2 className="h-8 w-8 text-primary animate-spin mb-4" />
                  <p className="text-sm text-gray-500">{isAr ? 'جاري تحليل المستند...' : 'Analyzing document...'}</p>
                </div>
              )}

              {!loading && !result && (
                <div className="flex flex-col items-center justify-center py-16 text-center">
                  <div className="w-16 h-16 bg-amber-50 rounded-2xl flex items-center justify-center mb-4">
                    <Sparkles className="h-8 w-8 text-amber-600" strokeWidth={1.5} />
                  </div>
                  <p className="text-sm text-gray-500 max-w-xs">{isAr ? 'ارفع ملفاً أو الصق نصاً ليقوم الذكاء الاصطناعي باستخراج البيانات تلقائياً' : 'Upload a file or paste text for AI to extract data automatically'}</p>
                </div>
              )}

              {!loading && result && (
                <div className="space-y-4">
                  {/* Meta info */}
                  <div className="flex gap-3 text-xs">
                    <span className="px-2 py-1 bg-primary/10 text-primary rounded-lg">{result.extract_type || 'auto'}</span>
                    {result.filename && <span className="px-2 py-1 bg-gray-100 text-gray-600 rounded-lg">{result.filename}</span>}
                  </div>

                  {/* Structured display for parsed JSON */}
                  {parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? (
                    <div className="space-y-3">
                      {Object.entries(parsed).map(([key, value]) => {
                        if (value === null || value === undefined) return null;
                        if (typeof value === 'object' && !Array.isArray(value)) {
                          return (
                            <div key={key} className="p-3 bg-gray-50 rounded-xl">
                              <div className="text-xs font-semibold text-gray-500 uppercase mb-2">{key.replace(/_/g, ' ')}</div>
                              <div className="grid grid-cols-2 gap-2">
                                {Object.entries(value).map(([k, v]) => v != null && (
                                  <div key={k} className="text-sm">
                                    <span className="text-gray-400">{k.replace(/_/g, ' ')}:</span>{' '}
                                    <span className="font-medium text-gray-900">{String(v)}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          );
                        }
                        if (Array.isArray(value)) {
                          return (
                            <div key={key} className="p-3 bg-gray-50 rounded-xl">
                              <div className="text-xs font-semibold text-gray-500 uppercase mb-2">{key.replace(/_/g, ' ')}</div>
                              {value.map((item, i) => (
                                <div key={i} className="text-sm text-gray-700 py-1 border-b last:border-0">
                                  {typeof item === 'object' ? JSON.stringify(item) : String(item)}
                                </div>
                              ))}
                            </div>
                          );
                        }
                        return (
                          <div key={key} className="flex justify-between py-1.5 border-b border-gray-100 last:border-0">
                            <span className="text-sm text-gray-500">{key.replace(/_/g, ' ')}</span>
                            <span className="text-sm font-medium text-gray-900">{String(value)}</span>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="p-4 bg-gray-50 rounded-xl text-sm whitespace-pre-wrap font-mono leading-relaxed max-h-[400px] overflow-y-auto" dir="auto">
                      {typeof parsed === 'string' ? parsed : JSON.stringify(parsed, null, 2)}
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </Layout>
  );
}
